import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { METRIC_STALE_TIME } from "../constants";
import { api } from "../utils/apiClient";
import type { Airline, UserAirlineGroupsResponse } from "../types";

const USER_AIRLINE_GROUPS_ENDPOINT = "/api/v1/user-airline-groups";

const fetchUserAirlineGroups = async (): Promise<UserAirlineGroupsResponse> => {
  try {
    const res = await api.get(USER_AIRLINE_GROUPS_ENDPOINT);
    if (!res.ok) throw new Error(`Failed to load user airline groups: ${res.status}`);
    return res.json();
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 403) {
      window.location.replace("/unauthorized");
    }
    throw error;
  }
};

export const airlinesFromGroups = (groups: UserAirlineGroupsResponse): Airline[] =>
  groups.flatMap((group) => group.airlines);

export const userAirlineGroupsQueryOptions = {
  queryKey: [USER_AIRLINE_GROUPS_ENDPOINT],
  queryFn: fetchUserAirlineGroups,
  staleTime: METRIC_STALE_TIME,
};

export const useUserAirlineGroupsQuery = (enabled = true) => {
  return useQuery({ ...userAirlineGroupsQueryOptions, enabled });
};

export const useUserAirlineGroupsSuspenseQuery = (enabled = true) => {
  return useSuspenseQuery({
    ...userAirlineGroupsQueryOptions,
    queryFn: enabled ? fetchUserAirlineGroups : () => Promise.resolve<UserAirlineGroupsResponse>([]),
  });
};

export const useAirlinesQuery = () => {
  return useQuery({ ...userAirlineGroupsQueryOptions, select: airlinesFromGroups });
};
